import React, { useEffect, useState } from "react"
import {
  Alert,
  Button,
  Card,
  CardBody,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import Switch from "components/Shared/Switch"
import { get, post, put } from "helpers/api_helper"
import { showAlert } from "store/reducers/alertReducer"
import { useDispatch, useSelector } from "react-redux"
import isPermissionEnabled from "common/isPermissionEnabled"

const MASK = "••••••••"

const defaultValues = {
  auth: {
    socialProviders: {
      google: {
        enabled: false,
        webClientId: "",
        iosClientId: "",
        androidClientId: "",
      },
      apple: {
        enabled: false,
        serviceId: "",
      },
    },
    requireVerifiedEmail: true,
  },
  planAllowed: { google: false, apple: false },
  effective: { google: false, apple: false },
  keysMasked: true,
}

const isMaskedValue = value => String(value || "").trim() === MASK

const omitMaskedKeys = value => {
  if (Array.isArray(value)) {
    return value.map(item => omitMaskedKeys(item))
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, nested]) => [key, omitMaskedKeys(nested)])
        .filter(([, nested]) => nested !== undefined)
    )
  }
  if (isMaskedValue(value)) {
    return undefined
  }
  return value
}

const SocialAuthSettings = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { userRole, accountType } = useSelector(state => state.Login || {})
  const canView = isPermissionEnabled(
    userRole,
    53,
    "social_auth_settings_1",
    accountType
  )
  const canEdit = isPermissionEnabled(
    userRole,
    53,
    "social_auth_settings_3",
    accountType
  )

  document.title = t("Social Login Settings")

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formValues, setFormValues] = useState(defaultValues)
  const [keysRevealed, setKeysRevealed] = useState(false)
  const [viewStepUpToken, setViewStepUpToken] = useState(null)
  const [editStepUpToken, setEditStepUpToken] = useState(null)
  const [otpModal, setOtpModal] = useState({
    open: false,
    mode: null,
    otp: "",
  })
  const [otpRequestLoading, setOtpRequestLoading] = useState(false)
  const [otpVerifyLoading, setOtpVerifyLoading] = useState(false)

  const planAllowed = formValues.planAllowed || defaultValues.planAllowed
  const hasSocialLoginPlan =
    planAllowed.google === true || planAllowed.apple === true

  const loadConfig = async (stepUpToken = null) => {
    setLoading(true)
    try {
      const query = stepUpToken
        ? `?stepUpToken=${encodeURIComponent(stepUpToken)}`
        : ""
      const res = await get(`admin/auth-config/social-providers${query}`)
      if (res?.status !== false && res?.data) {
        const nextPlanAllowed = {
          ...defaultValues.planAllowed,
          ...(res.data.planAllowed || {}),
        }

        setFormValues({
          auth: {
            ...defaultValues.auth,
            ...res.data.auth,
            socialProviders: {
              google: {
                ...defaultValues.auth.socialProviders.google,
                ...(res.data.auth?.socialProviders?.google || {}),
              },
              apple: {
                ...defaultValues.auth.socialProviders.apple,
                ...(res.data.auth?.socialProviders?.apple || {}),
              },
            },
          },
          planAllowed: nextPlanAllowed,
          effective: {
            ...defaultValues.effective,
            ...(res.data.effective || {}),
          },
          keysMasked: res.data.keysMasked !== false,
        })
        setKeysRevealed(res.data.keysMasked === false)
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: res?.msg || t("Failed to load social login settings"),
          })
        )
      }
    } catch (_err) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Failed to load social login settings"),
        })
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      loadConfig()
    } else {
      setLoading(false)
    }
  }, [canView])

  const updateNested = (path, value) => {
    setFormValues(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      const keys = path.split(".")
      let cursor = next
      for (let i = 0; i < keys.length - 1; i += 1) {
        cursor = cursor[keys[i]]
      }
      cursor[keys[keys.length - 1]] = value
      return next
    })
  }

  const closeOtpModal = () => {
    setOtpModal({ open: false, mode: null, otp: "" })
  }

  const openOtpModal = mode => {
    setOtpModal({ open: true, mode, otp: "" })
  }

  const requestOtp = async mode => {
    const path =
      mode === "edit"
        ? "admin/auth-config/social-providers/step-up/edit"
        : "admin/auth-config/social-providers/step-up/view"
    setOtpRequestLoading(true)
    try {
      const res = await post(path, {})
      if (res?.status !== false) {
        dispatch(
          showAlert({
            type: "success",
            message: res?.msg || t("Verification code sent to your email"),
          })
        )
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: res?.msg || t("Failed to send verification code"),
          })
        )
      }
    } catch (_err) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Failed to send verification code"),
        })
      )
    } finally {
      setOtpRequestLoading(false)
    }
  }

  const handleViewKeys = async () => {
    openOtpModal("view")
    await requestOtp("view")
  }

  const handleEditKeys = async () => {
    openOtpModal("edit")
    await requestOtp("edit")
  }

  const submitOtp = async e => {
    e.preventDefault()
    const trimmed = String(otpModal.otp || "").trim()
    if (!/^\d{6}$/.test(trimmed)) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Please enter a valid 6-digit OTP"),
        })
      )
      return
    }

    setOtpVerifyLoading(true)
    try {
      const res = await post("admin/auth-config/social-providers/step-up/verify", {
        otp: trimmed,
      })
      if (res?.status === false) {
        dispatch(
          showAlert({
            type: "error",
            message: res?.msg || t("Invalid code"),
          })
        )
        return
      }

      const token = res?.data?.stepUpToken
      const action = res?.data?.socialAuthAction
      const expectedAction =
        otpModal.mode === "edit"
          ? "edit_social_auth_keys"
          : "view_social_auth_keys"

      if (!token) {
        dispatch(
          showAlert({
            type: "error",
            message: t("Invalid verification response"),
          })
        )
        return
      }

      if (action && action !== expectedAction) {
        dispatch(
          showAlert({
            type: "error",
            message: t(
              "Verification did not match this action. Request a new code."
            ),
          })
        )
        return
      }

      if (otpModal.mode === "edit") {
        setEditStepUpToken(token)
        setViewStepUpToken(token)
      } else {
        setViewStepUpToken(token)
      }

      closeOtpModal()
      await loadConfig(token)
      dispatch(
        showAlert({
          type: "success",
          message: t("Identity verified"),
        })
      )
    } catch (_err) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Invalid code"),
        })
      )
    } finally {
      setOtpVerifyLoading(false)
    }
  }

  const buildAuthPayload = () => {
    const authPayload = JSON.parse(JSON.stringify(formValues.auth))
    if (keysRevealed) {
      const stripped = omitMaskedKeys(authPayload)
      return stripped
    }

    return {
      socialProviders: {
        google: { enabled: authPayload.socialProviders.google.enabled },
        apple: { enabled: authPayload.socialProviders.apple.enabled },
      },
      requireVerifiedEmail: authPayload.requireVerifiedEmail,
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!canEdit) {
      return
    }

    const authPayload = buildAuthPayload()
    const includesSecrets =
      keysRevealed &&
      (authPayload.socialProviders?.google?.webClientId !== undefined ||
        authPayload.socialProviders?.google?.iosClientId !== undefined ||
        authPayload.socialProviders?.google?.androidClientId !== undefined ||
        authPayload.socialProviders?.apple?.serviceId !== undefined)

    if (includesSecrets && !editStepUpToken) {
      dispatch(
        showAlert({
          type: "error",
          message: t(
            "Verify your identity to update social login keys before saving."
          ),
        })
      )
      return
    }

    setSaving(true)
    try {
      const body = { auth: authPayload }
      if (includesSecrets) {
        body.stepUpToken = editStepUpToken
      }

      const res = await put("admin/auth-config/social-providers", body)
      if (res?.status !== false) {
        dispatch(
          showAlert({
            type: "success",
            message: res?.msg || t("Social login settings updated"),
          })
        )
        setEditStepUpToken(null)
        setViewStepUpToken(null)
        setKeysRevealed(false)
        await loadConfig()
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: res?.msg || t("Failed to update social login settings"),
          })
        )
      }
    } catch (_err) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Failed to update social login settings"),
        })
      )
    } finally {
      setSaving(false)
    }
  }

  const google = formValues.auth.socialProviders.google
  const apple = formValues.auth.socialProviders.apple
  const effective = formValues.effective || defaultValues.effective
  const keysEditable = keysRevealed && Boolean(editStepUpToken)

  const renderGoogleSection = () => {
    if (!planAllowed.google) {
      return null
    }

    return (
      <>
        <FormGroup className="d-flex justify-content-between align-items-center">
          <Label className="mb-0">{t("Google Sign-In")}</Label>
          <Switch
            isChecked={google.enabled}
            onClickHandler={() =>
              updateNested(
                "auth.socialProviders.google.enabled",
                !google.enabled
              )
            }
            disabled={!canEdit}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("Google Web Client ID")}</Label>
          <Input
            value={google.webClientId}
            onChange={e =>
              updateNested(
                "auth.socialProviders.google.webClientId",
                e.target.value
              )
            }
            disabled={!keysEditable}
            placeholder={
              google.webClientIdConfigured && !keysRevealed
                ? MASK
                : "xxx.apps.googleusercontent.com"
            }
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("Google iOS Client ID")}</Label>
          <Input
            value={google.iosClientId}
            onChange={e =>
              updateNested(
                "auth.socialProviders.google.iosClientId",
                e.target.value
              )
            }
            disabled={!keysEditable}
            placeholder={
              google.iosClientIdConfigured && !keysRevealed ? MASK : ""
            }
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("Google Android Client ID")}</Label>
          <Input
            value={google.androidClientId}
            onChange={e =>
              updateNested(
                "auth.socialProviders.google.androidClientId",
                e.target.value
              )
            }
            disabled={!keysEditable}
            placeholder={
              google.androidClientIdConfigured && !keysRevealed ? MASK : ""
            }
          />
        </FormGroup>
      </>
    )
  }

  const renderAppleSection = () => {
    if (!planAllowed.apple) {
      return null
    }

    return (
      <>
        {planAllowed.google ? <hr /> : null}
        <FormGroup className="d-flex justify-content-between align-items-center">
          <Label className="mb-0">{t("Apple Sign-In")}</Label>
          <Switch
            isChecked={apple.enabled}
            onClickHandler={() =>
              updateNested(
                "auth.socialProviders.apple.enabled",
                !apple.enabled
              )
            }
            disabled={!canEdit}
          />
        </FormGroup>

        <FormGroup>
          <Label>{t("Apple Service ID")}</Label>
          <Input
            value={apple.serviceId}
            onChange={e =>
              updateNested(
                "auth.socialProviders.apple.serviceId",
                e.target.value
              )
            }
            disabled={!keysEditable}
            placeholder={
              apple.serviceIdConfigured && !keysRevealed
                ? MASK
                : "com.example.signin"
            }
          />
        </FormGroup>
      </>
    )
  }

  if (!canView) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Alert color="warning">{t("You do not have permission to view this page.")}</Alert>
        </Container>
      </div>
    )
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Card>
              <CardBody>
                {!hasSocialLoginPlan ? (
                  <Alert color="warning" className="mb-3">
                    {t(
                      "Social login is not included in your subscription plan. Contact your platform administrator to enable Google or Apple on your plan."
                    )}
                  </Alert>
                ) : (
                  <>
                    {!(effective.google || effective.apple) ? (
                      <Alert color="info" className="mb-3">
                        {t(
                          "Enable a provider below. OAuth keys are hidden until you verify with OTP."
                        )}
                      </Alert>
                    ) : null}

                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <Button
                        color="outline-primary"
                        type="button"
                        disabled={otpRequestLoading || keysRevealed}
                        onClick={handleViewKeys}
                      >
                        {otpRequestLoading && otpModal.mode === "view" ? (
                          <Spinner size="sm" />
                        ) : (
                          t("View keys")
                        )}
                      </Button>
                      {canEdit ? (
                        <Button
                          color="outline-secondary"
                          type="button"
                          disabled={otpRequestLoading}
                          onClick={handleEditKeys}
                        >
                          {otpRequestLoading && otpModal.mode === "edit" ? (
                            <Spinner size="sm" />
                          ) : (
                            t("Unlock to edit keys")
                          )}
                        </Button>
                      ) : null}
                    </div>

                    {formValues.keysMasked ? (
                      <Alert color="light" className="small border">
                        {t(
                          "OAuth client IDs and Apple Service ID are masked. Use View keys or Unlock to edit keys after email OTP verification."
                        )}
                      </Alert>
                    ) : null}

                    {renderGoogleSection()}
                    {renderAppleSection()}

                    {(planAllowed.google || planAllowed.apple) && (
                      <>
                        <hr />
                        <FormGroup className="d-flex justify-content-between align-items-center">
                          <Label className="mb-0">
                            {t("Require verified email")}
                          </Label>
                          <Switch
                            isChecked={formValues.auth.requireVerifiedEmail}
                            onClickHandler={() =>
                              updateNested(
                                "auth.requireVerifiedEmail",
                                !formValues.auth.requireVerifiedEmail
                              )
                            }
                            disabled={!canEdit}
                          />
                        </FormGroup>
                      </>
                    )}
                  </>
                )}

                {canEdit && hasSocialLoginPlan && (
                  <div className="text-end mt-3">
                    <Button color="primary" type="submit" disabled={saving}>
                      {saving ? t("Saving...") : t("Save settings")}
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          </Form>
        )}
      </Container>

      <Modal isOpen={otpModal.open} toggle={closeOtpModal} backdrop="static">
        <Form onSubmit={submitOtp}>
          <ModalHeader toggle={closeOtpModal}>{t("Verify OTP")}</ModalHeader>
          <ModalBody>
            <p className="text-muted">
              {otpModal.mode === "edit"
                ? t(
                    "Verify your identity to edit social login keys. Enter the 6-digit code sent to your email."
                  )
                : t(
                    "Verify your identity to view social login keys. Enter the 6-digit code sent to your email."
                  )}
            </p>
            <FormGroup>
              <Label for="socialAuthStepUpOtp">{t("Enter 6-digit OTP")}</Label>
              <Input
                id="socialAuthStepUpOtp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpModal.otp}
                disabled={otpVerifyLoading}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 6)
                  setOtpModal(prev => ({ ...prev, otp: v }))
                }}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter className="d-flex flex-wrap gap-2 justify-content-between">
            <Button
              color="link"
              className="p-0"
              type="button"
              disabled={otpRequestLoading || otpVerifyLoading}
              onClick={() => requestOtp(otpModal.mode)}
            >
              {otpRequestLoading ? <Spinner size="sm" /> : t("Resend code")}
            </Button>
            <div className="d-flex gap-2">
              <Button
                color="secondary"
                type="button"
                onClick={closeOtpModal}
                disabled={otpVerifyLoading}
              >
                {t("Cancel")}
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={otpVerifyLoading || otpModal.otp.length !== 6}
              >
                {otpVerifyLoading ? <Spinner size="sm" /> : t("Verify")}
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  )
}

export default SocialAuthSettings
